import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  AccessPermissionService,
  coalesceBlank,
  coalesceNullish,
  ComponentConfiguration,
  commonEnum,
  ComponentDetails,
  ConversionProcessItem,
  CountryWithRegion,
  CustomModalService,
  DistanceTypeEnum,
  ExcelImportTemplateViewModel,
  GeneralInformation,
  ImportProject,
  ImportTypeEnum,
  mainModuleName,
  MaterialDetails,
  PackagingLevelDetail,
  PackagingLevelDetailScenario,
  ProjectImportInfo,
  ProjectScope,
  rolesModuleName,
  Scenario,
  ScenarioConfiguration,
  ScenarioSpecificData,
  ShareOption,
  TransportDetails,
} from '@piqet-angular/shared';
import { AdministratorService } from 'libs/data/src/lib/services/administrator/administrator.service';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { ImportDataService } from 'libs/features/src/lib/layout/my-organization/import-project-components/import-data.service';
import { CookieService } from 'ngx-cookie-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { tap } from 'rxjs';
import * as XLSX from 'xlsx';
type ProjectScopeType =
  | 'Simple retail package ignoring product'
  | 'Retail, traded and pallet package supply chain logistics'
  | 'Full package and product LCA';

interface ErrorGroup {
  sheetName: string;
  errors: Array<{
    category: string;
    sheetName: string;
    location: string;
    message: string;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Feature flag: backwards-compatible import for the OLD project Excel template.
//
//   true  → BOTH old and new template formats are accepted. The component
//           header row is auto-inspected and the matching parser is used.
//   false → only the NEW template format is accepted. Old templates fall
//           through to the new parser, which will produce header / column
//           validation errors.
//
// Set this to false (and delete parseComponentRowOld + the OLD branches in
// validateParsedScenarioMaterialData) once the old template is fully retired.
// ─────────────────────────────────────────────────────────────────────────────
const SUPPORT_OLD_IMPORT_TEMPLATE = true;

@Component({
  selector: 'piqet-angular-upload-import',
  templateUrl: './upload-import.component.html',
  styleUrls: ['./upload-import.component.scss'],
})
export class UploadImportComponent {
  modalLabel: any;
  isShowChange = false;
  isImport: any;
  selectedFiles: any;
  imageUrl: any;
  fileBase64: any;
  importForm!: FormGroup;
  submitted = false;
  organizationList: any;
  formDataPayload: any;
  extFile = false;
  disableSubmit = true;
  selectedImportList$ = this.administratorService.selectedImportList$.pipe(
    tap((res: any) => {
      this.importForm.patchValue({ organization: res.data.organizationId });
    })
  );
  @Output() uploadImportProjectEvent = new EventEmitter<any>();
  @Output() uploadImportEvent = new EventEmitter<any>();
  @Output() importCustomExcelEvent = new EventEmitter<any>();
  generalInformation: GeneralInformation | any;
  scenarioSpecificData: ScenarioSpecificData | any;
  componentDetails: ComponentDetails | any;
  importProject: ImportProject | any;
  importErrors: any = [];
  jsonErrorMessages: any;
  shareOption = ShareOption;
  projectScope = ProjectScope;
  isLoading: boolean = false;
  projectImportInfo: ProjectImportInfo = new ProjectImportInfo();
  masterData: any;
  scopePresetId: any;
  payloadPackagingLCStages: any = [];
  payloadProductWestModeling: any = [];
  payloadProductLCStages: any = [];
  payloadPackagingLevels: any = [];
  importProjectData: any;
  jsonData: any;
  excelData: any = [];
  startRow: any;
  endRow: any;
  startRowTouched = false;
  endRowTouched = false;
  importLogListData: any;
  selectedTemplate: any;
  templateTouched = false;
  weightTypes = [
    { id: 1, label: 'Ton (t)', value: 'ton' },
    { id: 2, label: 'Kg (kg)', value: 'kg' },
    { id: 3, label: 'Pounds (lbs)', value: 'pounds' },
    { id: 4, label: 'Grams (g)', value: 'grams' },
    { id: 5, label: 'Ounces (oz)', value: 'ounces' },
    { id: 6, label: 'Milligrams (mg)', value: 'milligrams' },
    { id: 7, label: 'Stones (st)', value: 'stones' },
  ];
  selectedWeight: any = { id: 4, label: 'Grams (g)', value: 'grams' };
  weightTouched = false;
  importMessage: string = '';
  seaDistance: any;
  templateType: any;
  templateToggleType: any;
  ImportTypeEnum = ImportTypeEnum;
  checkPiqetTemplateAccess: boolean = false;
  checkCustomTemplateAccess: boolean = false;
  private countryCache: { [key: string]: any } = {};
  private distanceCache: { [key: string]: any } = {};

  constructor(
    private cdRef: ChangeDetectorRef,
    private spinnerService: NgxSpinnerService,
    private sharedService: SharedService,
    private importProjectDataService: ImportDataService,
    private projectService: ProjectsService,
    private http: HttpClient,
    private modal: CustomModalService,
    private formBuilder: FormBuilder,
    private administratorService: AdministratorService,
    private cookieService: CookieService,
    private accessService: AccessPermissionService
  ) {
    this.modalLabel = this.modal?.modalService?.config?.initialState?.['label'];
    this.isImport = this.modal?.modalService?.config?.initialState?.['importData'];
    this.templateType = this.modal?.modalService?.config?.initialState?.['type'];
    this.templateToggleType = this.templateType;
    this.checkPiqetTemplateAccess = this.isAccess('overview', 'PiqetTemplate');
    this.checkCustomTemplateAccess = this.isAccess('overview', 'CustomTemplate');
  }

  isAccess = (type: 'view' | 'edit' | 'create' | 'delete' | 'overview', templateName: string) => {
    return templateName == 'PiqetTemplate'
      ? this.accessService.hasModulePermission(
          rolesModuleName.piqetTemplate,
          mainModuleName.importPreferences,
          type
        )
      : this.accessService.hasModulePermission(
          rolesModuleName.customTemplate,
          mainModuleName.importPreferences,
          type
        );
  };

  async ngOnInit(): Promise<void> {
    this.importForm = this.formBuilder.group({
      organization: [null, Validators.required],
    });
    if (this.templateType == ImportTypeEnum.PiqetTemplate) {
      await this.getMasterDataForImportProject();
      await this.loadComingSoonText();
    }
    if (this.templateType == ImportTypeEnum.CustomTemplate) {
      await this.getImportCustomTemplates();
    }
  }

  loadComingSoonText(): void {
    this.http.get<any[]>('../../../assets/json/importProjectValidationMessages.json').subscribe(
      data => {
        this.jsonErrorMessages = data;
      },
      error => {
        console.error('Error loading coming soon:', error);
      }
    );
  }

  commonInputSearch(term: string, item: any) {
    return item.name?.toLowerCase().includes(term.toLowerCase());
  }

  async onFileSelected(event: any, fileInput: HTMLInputElement) {
    if (this.templateType == ImportTypeEnum.CustomTemplate) {
      let hasError = false;

      if (!this.selectedTemplate) {
        this.templateTouched = true;
        hasError = true;
      }
      if (!this.startRow || this.startRow < 1) {
        this.startRowTouched = true;
        hasError = true;
      }
      if (!this.endRow || this.endRow < 1) {
        this.endRowTouched = true;
        hasError = true;
      }

      if (hasError) {
        this.isLoading = false;
        fileInput.value = '';
        return;
      }
    }

    const file: File = event.target.files[0];

    this.importErrors = [];
    this.importMessage = `Fetching data from ${file.name}...`;
    // this.spinnerService.show();
    this.isLoading = true;

    if (!file) {
      fileInput.value = '';
      return this.handleFileProcessingEnd();
    }

    this.selectedFiles = file.name;
    const fileFormat = this.selectedFiles.split('.').pop().toLowerCase();

    if (!['xls', 'xlsx', 'csv', 'xlsm'].includes(fileFormat)) {
      this.extFile = true;
      this.selectedFiles = null;
      this.disableSubmit = false;
      fileInput.value = '';
      return this.handleFileProcessingEnd();
    }

    this.extFile = false;

    const base64File = await this.convertFileToBase64(file);
    this.importProjectDataService.setImportedFile({
      name: file.name,
      data: base64File,
      type: file.type,
    });

    this.extFile = false;

    try {
      if (this.templateType == ImportTypeEnum.PiqetTemplate) {
        //Piqet template
        const workbook = await this.readWorkbook(file);
        await this.processImportProject(workbook);
      } else if (this.templateType == ImportTypeEnum.MappingTemplate) {
        // Organization data mapping
        const workbook = await this.readWorkbook(file);
        await this.processMappingImport(workbook);
      } else if (this.templateType == ImportTypeEnum.CustomTemplate) {
        // Custom import template
        const workbook = await this.readWorkbook(file);
        await this.processCustomTemplate(workbook);
      } else {
        this.processDefaultImport(file); // Import master data
      }
    } catch (error) {
      this.importErrors.push({
        category: 'File Processing',
        message: 'An error occurred while processing the file. Please try again.',
      });
    } finally {
      this.handleFileProcessingEnd();
      fileInput.value = '';
    }
  }

  private async readWorkbook(file: File): Promise<XLSX.WorkBook> {
    const data = await this.readFileAsArrayBuffer(file);
    return XLSX.read(data, { type: 'array' });
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }
  async processCustomTemplate(workbook: XLSX.WorkBook) {
    const template = JSON.parse(this.selectedTemplate.columnMappings);
    const sheetName = template.SheetName.trim().toLowerCase();
    const countryType = coalesceBlank(template.CountryType, 1);

    const sheet = Object.keys(workbook.Sheets).find(
      name => name.trim().toLowerCase() === sheetName
    );

    if (!sheet) {
      this.importErrors.push({
        category: 'File Processing',
        message: `Sheet "${template.SheetName}" not found in the imported file. Please check the file and try again.`,
        sheetName: sheetName,
      });
      return [];
    }

    const sheetData = workbook.Sheets[sheet];

    const getCellValue = (column: string, row: number, defaultValue: any) => {
      const cellAddress = column + row;
      return sheetData[cellAddress] ? sheetData[cellAddress].v : defaultValue;
    };

    let scenarioIdCounter = 1;

    for (let row = this.startRow; row <= this.endRow; row++) {
      let projectData = new ExcelImportTemplateViewModel();
      projectData.CountryType = countryType;

      let isNextRowBlank = Object.keys(template.ProjectConfiguration).every(key => {
        const config = template.ProjectConfiguration[key];
        const value = getCellValue(config.ColumnAddress, row, null);
        return value === null || value === '' || value === undefined;
      });

      if (isNextRowBlank) {
        break;
      }

      Object.keys(template.ProjectConfiguration).forEach(key => {
        const config = template.ProjectConfiguration[key];
        let value = config.PresentInExcel
          ? getCellValue(config.ColumnAddress, row, config.DefaultValue)
          : config.DefaultValue;

        if (config.PresentInExcel && (value === null || value === '' || value === undefined)) {
          this.importErrors.push({
            category: 'File Template',
            location: `${config.ColumnAddress} ${row}`,
            message: `Missing required value for "${key}" in column "${config.ColumnAddress}".`,
            sheetName: sheetName,
          });
        }

        if (value !== null) {
          if (Array.isArray(value)) {
            value = value.join(', ');
          } else if (typeof value === 'number') {
            value = value.toString();
          }
          projectData[key] = value;
        }

        if (!config.PresentInExcel) {
          if (typeof config.DefaultValue === 'number' || Array.isArray(config.DefaultValue)) {
            Object.keys(projectData).forEach(modelKey => {
              if (modelKey.startsWith(key) && modelKey.endsWith('Id')) {
                projectData[modelKey] = config.DefaultValue;
              }
            });
          }
        } else if (config.PresentInExcel) {
          if (key == 'PackagingConfigurationId') {
            projectData[key] = [0];
          }
          if (key == 'ProcessLocationId') {
            projectData[key] = [0];
          }
        }
      });

      projectData.Scenarios = template.ScenarioConfiguration.map((scenario: any) => {
        let scenarioData = new ScenarioConfiguration();
        scenarioData.Id = scenarioIdCounter;
        let componentIdCounter = 1;

        Object.keys(scenario).forEach(key => {
          if (key !== 'ComponentConfiguration') {
            const config = scenario[key];
            let value = config.PresentInExcel
              ? getCellValue(config.ColumnAddress, row, config.DefaultValue)
              : config.DefaultValue;

            if (config.PresentInExcel && (value === null || value === '' || value === undefined)) {
              this.importErrors.push({
                category: 'File Template',
                location: `${config.ColumnAddress} ${row}`,
                message: `Missing required value for "${key}" in column "${config.ColumnAddress}".`,
                sheetName: sheetName,
              });
            }

            if (value !== null) {
              if (Array.isArray(value)) {
                value = value.join(', ');
              } else if (typeof value === 'number') {
                value = value.toString();
              }
              scenarioData[key] = value;
            }

            if (!config.PresentInExcel) {
              if (typeof config.DefaultValue === 'number' || Array.isArray(config.DefaultValue)) {
                Object.keys(scenarioData).forEach(scenarioKey => {
                  if (scenarioKey.startsWith(key) && scenarioKey.endsWith('Id')) {
                    scenarioData[scenarioKey] = config.DefaultValue;
                  }
                });
              }
            }
          }
        });

        scenarioData.Components = scenario.ComponentConfiguration.map((component: any) => {
          let componentData = new ComponentConfiguration();
          componentData.Id = componentIdCounter++;
          componentData.ScenarioId = scenarioIdCounter;

          Object.keys(component).forEach(key => {
            const config = component[key];
            let value = config.PresentInExcel
              ? getCellValue(config.ColumnAddress, row, config.DefaultValue)
              : config.DefaultValue;

            if (config.PresentInExcel && (value === null || value === '' || value === undefined)) {
              this.importErrors.push({
                category: 'File Template',
                location: `${config.ColumnAddress}${row}`,
                message: `Missing required value for "${key}" in column "${config.ColumnAddress}".`,
                sheetName: sheetName,
              });
            }

            if (value !== null) {
              if (Array.isArray(value)) {
                value = value.join(', ');
              } else if (typeof value === 'number') {
                value = value.toString();
              }
              componentData[key] = value;
            }

            if (!config.PresentInExcel) {
              if (typeof config.DefaultValue === 'number' || Array.isArray(config.DefaultValue)) {
                Object.keys(componentData).forEach(componentKey => {
                  if (componentKey.startsWith(key) && componentKey.endsWith('Id')) {
                    componentData[componentKey] = config.DefaultValue;
                  }
                });
              }
            }

            if (key === 'Weight' && value !== null) {
              componentData[key] = this.convertWeight(value, this.selectedWeight.value);
            }
          });

          return componentData;
        });

        scenarioIdCounter++;
        return scenarioData;
      });

      this.excelData.push(projectData);
    }
  }

  convertWeight(value: number, weightType: string): number {
    switch (weightType) {
      case 'ton':
        return value * 1e6;
      case 'kg':
        return value * 1000;
      case 'pounds':
        return value / 0.00220462;
      case 'ounces':
        return value / 0.035274;
      case 'milligrams':
        return value / 1000;
      case 'stones':
        return value / 0.000157473;
      case 'grams':
      default:
        return value;
    }
  }

  private async processImportProject(workbook: XLSX.WorkBook) {
    const sheetName = 'Project';
    const worksheet = workbook.Sheets[sheetName];

    if (!worksheet) {
      this.importErrors.push({
        category: 'File Template',
        location: 'A1',
        message:
          "The selected file's template does not match the required import project format. Please check the file structure and try again.",
        sheetName: sheetName,
      });
      return;
    }

    const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    const cellValue = this.getCellValue(jsonData, 0, 0);

    const validTemplates = [
      'Project Import Sheet\r\nGeneral information',
      'Project Import Sheet\nGeneral information',
    ];
    if (!validTemplates.includes(cellValue)) {
      this.importErrors.push({
        category: 'File Template',
        location: 'A1',
        message: "The selected file's template does not match the required import project format.",
        sheetName: sheetName,
      });
      return;
    }

    const parsedGeneralData: GeneralInformation = {
      projectName: coalesceNullish(jsonData[1][1], ''),
      share: coalesceNullish(jsonData[1][6], ''),
      teamName: coalesceNullish(jsonData[1][8], ''),
      defaultCountry: coalesceNullish(jsonData[1][10], ''),
      processLocations: coalesceNullish(jsonData[1][13], ''),
      turnOnCosts: coalesceNullish(jsonData[2][1], ''),
      projectScope: coalesceNullish(jsonData[2][6], ''),
      productLoss: coalesceNullish(jsonData[2][9], ''),
      goalAndScope: coalesceNullish(jsonData[2][12], ''),
    };

    await this.validateParsedData(parsedGeneralData);
    await this.changeScope(parsedGeneralData.projectScope);

    this.importProject = new ImportProject();
    this.generalInformation = parsedGeneralData;
    this.importProject.generalInformation = parsedGeneralData;

    const scenarioSheets = workbook.SheetNames.filter(sheet => sheet.toLowerCase().includes('s_'));
    let scenarioCount = 1;

    for (const scenarioSheetName of scenarioSheets) {
      const worksheet = workbook.Sheets[scenarioSheetName];
      const scenarioJsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (scenarioJsonData.length === 0) continue;

      const parsedScenarioSpecificData: ScenarioSpecificData = await this.parseScenarioData(
        scenarioJsonData,
        scenarioSheetName,
        scenarioCount,
        parsedGeneralData.processLocations
      );

      if (parsedScenarioSpecificData.scenarioName) {
        this.importProject.scenarioSpecificData.push(parsedScenarioSpecificData);
      }
      scenarioCount++;
    }

    if (this.importErrors.length === 0) {
      localStorage.setItem('importProjectData', JSON.stringify(this.importProject));
      await this.mapProjectData();
    }
  }

  private async processMappingImport(workbook: XLSX.WorkBook) {
    const sheetName = 'Mapping';
    const worksheet = workbook.Sheets[sheetName];

    if (!worksheet) {
      throw new Error(`Sheet "${sheetName}" not found.`);
    }

    const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    this.jsonData = [];
    this.importErrors = [];

    for (let i = 3; i < jsonData.length; i++) {
      const row = jsonData[i];

      if (
        !row.some(
          (cell: any) => cell !== null && cell !== undefined && cell.toString().trim() !== ''
        )
      ) {
        break;
      }

      const name = row[0] ? row[0].toString().trim() : null;
      const materialName = row[1] ? row[1].toString().trim() : null;
      const conversionName = row[2] ? row[2].toString().trim() : null;

      if (!name) {
        this.importErrors.push({
          category: 'Mapping Name',
          location: this.getCellLocation(i + 1, 1),
          message: `Missing required "Name" value in row ${i + 1}.`,
          sheetName: sheetName,
        });
        continue;
      }
      if (!materialName && !conversionName) {
        this.importErrors.push({
          category: 'Mapping Details',
          location: this.getCellLocation(i + 1, 2),
          message: `Both "Material Name" and "Conversion Name" are missing in row ${
            i + 1
          }. At least one is required.`,
          sheetName: sheetName,
        });
        continue;
      }

      this.jsonData.push({ name, materialName, conversionName });
    }
  }

  private processDefaultImport(file: File) {
    this.selectedFiles = file.name;

    this.isShowChange = !!this.selectedFiles;
    const formData: FormData = new FormData();
    const organizationID = JSON.parse(this.cookieService.get('organizationId') as string);

    formData.append('OrganizationId', organizationID);
    formData.append('ExcelFile', file);
    this.formDataPayload = formData;
  }

  private handleFileProcessingEnd() {
    this.isLoading = false;
    this.importMessage = `Successfully fetched data from ${this.selectedFiles}`;
    setTimeout(() => this.spinnerService.hide(), 8000);
  }

  readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  async parseScenarioData(
    jsonData: any[],
    sheetName: string,
    scenarioCount: number,
    processLocation: any
  ): Promise<ScenarioSpecificData> {
    const scenarioIndex = 2;
    const palletizationCell = 48; // Row 49
    const inputCell = 56; // Row 57
    const supplierItemCell = 67; // Row 68
    const packCodeCell = 68; // Row 69
    const parsedScenarioSpecificData: ScenarioSpecificData = {
      scenarioName: coalesceNullish(jsonData[scenarioIndex][1], ''),
      supplierName: coalesceNullish(jsonData[scenarioIndex][6], ''),
      nominalCapacity: coalesceNullish(jsonData[scenarioIndex][9], ''),
      piqetVersion: coalesceNullish(jsonData[scenarioIndex][12], ''),
      description: coalesceNullish(jsonData[scenarioIndex + 1][1], ''),
      productSectorData: '',
      massVolumeRetailPack: coalesceNullish(jsonData[scenarioIndex + 1][6], ''),
      annualRetailVolumeUnits: coalesceNullish(jsonData[scenarioIndex + 1][9], ''),
      scenarioNamePackaging: '',
      compReference: '',
      qpPackComponentCode: '',
      piqetProjectCode: '',
      year: coalesceNullish(jsonData[scenarioIndex + 1][12], ''),
      uuid: coalesceNullish(jsonData[scenarioIndex + 2][1], ''),
      department: coalesceNullish(jsonData[scenarioIndex + 2][6], ''),
      category: coalesceNullish(jsonData[scenarioIndex + 2][9], ''),
      subCategory: coalesceNullish(jsonData[scenarioIndex + 2][12], ''),
      endOfLifeCountry: coalesceNullish(jsonData[scenarioIndex + 3][1], ''),
      unitsPerBox: coalesceNullish(jsonData[palletizationCell][0], ''),
      boxesPerLayer: coalesceNullish(jsonData[palletizationCell][2], ''),
      layersPerPallet: coalesceNullish(jsonData[palletizationCell][4], ''),
      totalWeightPerBox: coalesceNullish(jsonData[palletizationCell][6], ''),
      totalHeight: coalesceNullish(jsonData[palletizationCell][8], ''),
      totalWeight: coalesceNullish(jsonData[palletizationCell][10], ''),
      supplierComments: coalesceNullish(jsonData[palletizationCell + 2][0], ''),
      productType: coalesceNullish(jsonData[inputCell][1], ''),
      packDescription: coalesceNullish(jsonData[inputCell][3], ''),
      material: coalesceNullish(jsonData[inputCell][4], ''),
      renewableEnergy: coalesceNullish(jsonData[inputCell][6], ''),
      manufacturedEMEA: coalesceNullish(jsonData[inputCell][7], ''),
      supplierItemCode: coalesceNullish(jsonData[supplierItemCell][3], ''),
      packCode: coalesceNullish(jsonData[packCodeCell][3], ''),
      componentDetails: [],
      packagingLevelDetail: [],
    };
    await this.validateParsedScenarioData(
      parsedScenarioSpecificData,
      scenarioIndex + 1,
      scenarioCount,
      sheetName,
      processLocation
    );

    await this.parsePackagingLevelDetails(jsonData, parsedScenarioSpecificData, scenarioIndex);
    await this.parseComponentDetails(
      jsonData,
      parsedScenarioSpecificData,
      scenarioIndex,
      sheetName,
      processLocation
    );

    return parsedScenarioSpecificData;
  }

  // Method to parse packaging level details
  private async parsePackagingLevelDetails(
    jsonData: any[],
    parsedScenarioSpecificData: ScenarioSpecificData,
    scenarioIndex: number
  ) {
    const parsedPackagingLevels: PackagingLevelDetail[] = [];

    let componentIndex = scenarioIndex + 6;
    while (componentIndex < scenarioIndex + 11) {
      const row = jsonData[componentIndex];
      if (row && row.length > 0) {
        // Treat the packaging level as active only when Excel says "Yes"
        // AND the Quantity column is a positive number. A row with Used=Yes
        // but Quantity=0 is considered inactive (the user effectively didn't
        // use that level even though the Yes cell was left in).
        const usedCellSaysYes = row[2]?.toLowerCase() === 'yes';
        const amount = coalesceBlank(row[4], 1);
        const amountAsNumber = Number(amount);
        const hasPositiveQuantity = Number.isFinite(amountAsNumber) && amountAsNumber > 0;

        const parsedPackagingLevelDetail: PackagingLevelDetail = {
          isActive: usedCellSaysYes && hasPositiveQuantity,
          name: coalesceNullish(row[0], ''),
          packagingLevelName: coalesceNullish(row[1], ''),
          imageName: coalesceNullish(row[1], ''),
          amount: amount,
          packagingLevelDetail: '',
          totalunitsperPallet: 1,
          noOfUnit: coalesceBlank(row[9], 1),
          packagingLevelId: 0,
          isLastChild: false,
        };
        parsedPackagingLevels.push(parsedPackagingLevelDetail);
      }
      componentIndex++;
    }
    parsedPackagingLevels.forEach((level, index, array) => {
      let totalUnits = level.amount;

      for (let i = index + 1; i < array.length; i++) {
        totalUnits *= array[i].amount;
      }

      level.totalunitsperPallet = totalUnits;
      level.isLastChild = index === array.length - 1;

      if (!level.isLastChild) {
        level.packagingLevelDetail = `There are ${level.amount} ${
          level.name
        } in a ${coalesceNullish(array[index + 1]?.name, 'Pallet')}`;
      } else {
        level.packagingLevelDetail = '';
        level.totalunitsperPallet = 0;
      }
    });

    parsedScenarioSpecificData.packagingLevelDetail.push(...parsedPackagingLevels);
  }

  // Method to parse component details
  // Auto-detects whether the scenario sheet uses the NEW template (with the
  // "Area based conversion" checkbox column at H and "Area cm2" at K) or the
  // OLD template (no area columns). The detection is based on the components
  // header row (row 15 in 1-based, scenarioIndex + 12 in 0-based).
  //
  // Honors the SUPPORT_OLD_IMPORT_TEMPLATE feature flag — when the flag is
  // off, this always returns 'new' (no backwards-compat parsing).
  //
  // Returns 'new' if col H or col K headers contain "Area based conversion" /
  // "Area cm2" text, otherwise 'old'.
  private detectComponentTemplateVersion(jsonData: any[], scenarioIndex: number): 'old' | 'new' {
    if (!SUPPORT_OLD_IMPORT_TEMPLATE) return 'new';

    const headerRow = jsonData[scenarioIndex + 12];
    if (!headerRow || !Array.isArray(headerRow)) return 'old';

    const norm = (v: any) =>
      String(v ?? '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    // NEW template signature: col H = "Area based conversion"
    // OLD template signature: col H = "Conversion country" (and there are
    // no transport leg columns at all in the old template).
    const colH = norm(headerRow[7]);
    const colK = norm(headerRow[10]);

    if (colH === 'area based conversion' || colK === 'area cm2') {
      return 'new';
    }
    return 'old';
  }

  private async parseComponentDetails(
    jsonData: any[],
    parsedScenarioSpecificData: ScenarioSpecificData,
    scenarioIndex: number,
    sheetName: string,
    processLocation: any
  ) {
    const templateVersion = this.detectComponentTemplateVersion(jsonData, scenarioIndex);
    let componentIndex = scenarioIndex + 13;

    while (componentIndex < scenarioIndex + 43) {
      const row = jsonData[componentIndex];
      if (!row || row.length === 0) break;

      // Check if the row has actual data (skip rows that only have checkbox values).
      // A valid component row must have at least Type (col 0) or Component name (col 1).
      // Explicit boolean test (instead of `row[0] || row[1]`) keeps Sonar happy.
      const hasData = Boolean(row[0]) || Boolean(row[1]);
      if (!hasData) break;

      const parsedComponentDetails =
        templateVersion === 'new' ? this.parseComponentRowNew(row) : this.parseComponentRowOld(row);
      parsedComponentDetails.templateVersion = templateVersion;

      const matchingPackaging = parsedScenarioSpecificData.packagingLevelDetail.find(
        pkg =>
          pkg.name?.toLowerCase().trim() === parsedComponentDetails.type?.toLowerCase().trim() &&
          pkg.isActive
      );

      if (!matchingPackaging) {
        const msg = this.jsonErrorMessages['componentType_mismatch'].replace(
          '{0}',
          parsedComponentDetails.type
        );
        this.importErrors.push({
          category: 'Component Packaging Level',
          location: this.getCellLocation(componentIndex + 1, 1),
          message: msg,
          sheetName: sheetName,
        });
      }

      await this.validateParsedScenarioMaterialData(
        parsedComponentDetails,
        componentIndex + 1,
        processLocation,
        sheetName
      );

      parsedScenarioSpecificData.componentDetails.push(parsedComponentDetails);
      componentIndex++;
    }
  }

  // NEW template (with area-based conversion columns) component row layout:
  //  A=Type B=Name C=Material D=RecyclingPathway E=Weight F=Recycled%
  //  G=MaterialCountry
  //  Conversion blocks (4 cols each, repeated CONVERSION_BLOCK_COUNT times):
  //    Conv 1: H=AreaBased I=ConversionCountry J=ConversionProcess K=AreaCm2
  //    Conv 2: L=AreaBased M=ConversionCountry N=ConversionProcess O=AreaCm2
  //    [extend by adding 4 more cols per process and bumping
  //     CONVERSION_BLOCK_COUNT — every column after the conversions shifts
  //     automatically because their indices are derived from this constant.]
  //  After the conversion blocks (with CONVERSION_BLOCK_COUNT=2):
  //    P=Reuse Q=NoOfTrips
  //    R=TransportMode (leg1) ... T=Origin ... V=Destination
  //    Leg 2+: stride 4 starting at col X (index 23) — Mode + empty + Destination + empty
  private static readonly CONVERSION_START_COL = 7; // col H (0-based)
  private static readonly CONVERSION_BLOCK_SIZE = 4; // AreaBased + Country + Process + AreaCm2
  private static readonly CONVERSION_BLOCK_COUNT = 2; // current template — bump to 5 when columns are added
  private static readonly TRANSPORT_LEG_STRIDE = 4;

  private parseComponentRowNew(row: any[]): ComponentDetails {
    const C = UploadImportComponent.CONVERSION_START_COL;
    const B = UploadImportComponent.CONVERSION_BLOCK_SIZE;
    const N = UploadImportComponent.CONVERSION_BLOCK_COUNT;

    // Column indices derived from the conversion-block layout. When the
    // template adds more conversion processes, only CONVERSION_BLOCK_COUNT
    // changes — these stay correct.
    const reuseCol = C + N * B;
    const noOfTripsCol = reuseCol + 1;
    const leg1ModeCol = reuseCol + 2;
    const leg1OriginCol = reuseCol + 4;
    const leg1DestCol = reuseCol + 6;
    const leg2StartCol = reuseCol + 8;

    const isYes = (raw: any): boolean =>
      raw === true || raw === 1 || (typeof raw === 'string' && raw.trim().toLowerCase() === 'yes');

    const parseAreaCm2 = (raw: any, isAreaBased: boolean): number => {
      const n = Number(raw);
      return isAreaBased && !isNaN(n) ? n : 0;
    };

    // Read every conversion block. Empty blocks (no country and no process)
    // are skipped so we don't carry blank legs through the pipeline.
    const conversionLegs: any[] = [];
    for (let i = 0; i < N; i++) {
      const base = C + i * B;
      const isAreaBased = isYes(row[base]);
      const country = String(coalesceNullish(row[base + 1], '')).trim();
      const process = String(coalesceNullish(row[base + 2], '')).trim();
      const areaCm2 = parseAreaCm2(row[base + 3], isAreaBased);
      if (country || process || isAreaBased) {
        conversionLegs.push({
          isAreaBasedConversion: isAreaBased,
          conversionPlace: country,
          conversionProcess: process,
          areaCm2,
          column: { areaBased: base, country: base + 1, process: base + 2, areaCm2: base + 3 },
        });
      }
    }

    // Backward-compat fields: downstream code currently consumes a single
    // conversion. Use the first non-empty leg (or zeroed defaults).
    const primary = conversionLegs[0] ?? {
      isAreaBasedConversion: false,
      conversionPlace: '',
      conversionProcess: '',
      areaCm2: 0,
    };

    // Transport legs.
    const transportLegs: any[] = [];
    if (row[leg1ModeCol]) {
      transportLegs.push({
        transportMode: coalesceNullish(row[leg1ModeCol], ''),
        originLocation: coalesceNullish(row[leg1OriginCol], ''),
        destinationLocation: coalesceNullish(row[leg1DestCol], ''),
      });
    }
    let legCol = leg2StartCol;
    while (legCol < row.length) {
      const tm = row[legCol];
      if (!tm) break;
      transportLegs.push({
        transportMode: coalesceNullish(tm, ''),
        originLocation: '',
        destinationLocation: coalesceNullish(row[legCol + 2], ''),
      });
      legCol += UploadImportComponent.TRANSPORT_LEG_STRIDE;
    }

    return {
      type: coalesceNullish(row[0], ''),
      category: coalesceNullish(row[1], ''),
      componentName: coalesceNullish(row[1], ''),
      material: coalesceNullish(row[2], ''),
      recyclingPathway: coalesceNullish(row[3], ''),
      contentRecycled: coalesceNullish(row[3], ''),
      weightVirgin: coalesceNullish(row[4], ''),
      weightOfPCR: coalesceNullish(row[5], ''),
      materialOrigin: coalesceNullish(row[6], ''),
      isAreaBasedConversion: primary.isAreaBasedConversion,
      conversionPlace: primary.conversionPlace,
      conversionProcess: primary.conversionProcess,
      areaCm2: primary.areaCm2,
      conversionLegs: conversionLegs,
      reuse: coalesceNullish(row[reuseCol], ''),
      noOfTrips: coalesceNullish(row[noOfTripsCol], ''),
      transportLegs: transportLegs,
    } as ComponentDetails;
  }

  // OLD template component row layout (no area-based conversion columns):
  //  A=Type B=Name C=Material D=RecyclingPathway E=Weight F=Recycled%
  //  G=MaterialCountry H=ConversionCountry I=ConversionProcess J=Reuse K=NoOfTrips
  //  L=Transportation origin (port)  M=Transportation destination (port)
  //
  // The old template only supports a single transport leg with two PORT cells.
  // Each cell holds just a city name (e.g. "Durres", "Shengjin") — no state,
  // no country, no "(Port)" suffix in the text. We append " (Port)" before
  // sending the strings into the existing transport pipeline so the downstream
  // parser/resolver flow ports correctly via parseLocationString → isPort.
  private parseComponentRowOld(row: any[]): ComponentDetails {
    const transportLegs: any[] = [];

    const originRaw = row[11] != null ? String(row[11]).trim() : '';
    const destRaw = row[12] != null ? String(row[12]).trim() : '';

    // Wrap raw city names so the existing port flow picks them up.
    // parseLocationString → isPort is true when the string ends with "(Port)".
    const wrapAsPort = (cityOnly: string) => (cityOnly ? `${cityOnly} (Port)` : '');

    if (originRaw || destRaw) {
      transportLegs.push({
        // Old template doesn't capture transport mode — leave blank so
        // buildTransportFromLegs falls through to its default ("Ship, International").
        transportMode: '',
        originLocation: wrapAsPort(originRaw),
        destinationLocation: wrapAsPort(destRaw),
      });
    }

    return {
      type: coalesceNullish(row[0], ''),
      category: coalesceNullish(row[1], ''),
      componentName: coalesceNullish(row[1], ''),
      material: coalesceNullish(row[2], ''),
      recyclingPathway: coalesceNullish(row[3], ''),
      contentRecycled: coalesceNullish(row[3], ''),
      weightVirgin: coalesceNullish(row[4], ''),
      weightOfPCR: coalesceNullish(row[5], ''),
      materialOrigin: coalesceNullish(row[6], ''),
      isAreaBasedConversion: false,
      conversionPlace: coalesceNullish(row[7], ''),
      conversionProcess: coalesceNullish(row[8], ''),
      areaCm2: 0,
      reuse: coalesceNullish(row[9], ''),
      noOfTrips: coalesceNullish(row[10], ''),
      transportLegs: transportLegs,
    } as ComponentDetails;
  }

  getCellValue(data: any[], rowIndex: number, colIndex: number): string {
    return coalesceNullish(data?.[rowIndex]?.[colIndex]?.toString().trim(), '');
  }

  changeScope(scope: any) {
    this.importProjectDataService.updateProjectScope(scope);
  }

  changeProductLossScope(value: any) {
    this.importProjectDataService.updateProjectProductLoss(value);
  }

  validateParsedData(parsedData: GeneralInformation) {
    if (!parsedData.projectName || parsedData.projectName.trim().length === 0) {
      this.importErrors.push({
        category: 'Project Name',
        location: this.getCellLocation(2, 2),
        message: this.jsonErrorMessages['projectName_missing'],
        sheetName: 'Project',
      });
    } else if (parsedData.projectName.length > 100) {
      this.importErrors.push({
        category: 'Project Name',
        location: this.getCellLocation(2, 2),
        message: this.jsonErrorMessages['projectName_tooLong'],
        sheetName: 'Project',
      });
    }

    if (
      ![
        this.shareOption.NoOneKeepItPrivate,
        this.shareOption.EveryoneInMyOrganization,
        this.shareOption.OneOfMyTeam,
      ].includes(parsedData.share)
    ) {
      this.importErrors.push({
        category: 'Share Option',
        location: this.getCellLocation(2, 7),
        message: this.jsonErrorMessages['shareOption_invalid'],
        sheetName: 'Project',
      });
    }

    if (parsedData.share === this.shareOption.OneOfMyTeam) {
      if (!parsedData.teamName || parsedData.teamName.trim().length === 0) {
        this.importErrors.push({
          category: 'Team Name',
          location: this.getCellLocation(2, 9),
          message: this.jsonErrorMessages['teamName_missing'],
          sheetName: 'Project',
        });
      }
    }

    if (!parsedData.defaultCountry || parsedData.defaultCountry.trim() === '') {
      this.importErrors.push({
        category: 'Default Country',
        location: this.getCellLocation(2, 11),
        message: this.jsonErrorMessages['defaultCountry_missing'],
        sheetName: 'Project',
      });
    } else {
      if (
        parsedData.processLocations.length > 0 &&
        !parsedData.processLocations.includes(parsedData.defaultCountry)
      ) {
        this.importErrors.push({
          category: 'Process Locations',
          location: this.getCellLocation(2, 14),
          message: this.jsonErrorMessages['processLocations_defaultCountryMissing'],
          sheetName: 'Project',
        });
      }
    }

    const projectScope = parsedData.projectScope;
    if (projectScope === 'Retail, traded and pallet package supply chain logistics') {
      const productLoss = parsedData.productLoss;
      if (!productLoss || String(productLoss).trim().length === 0) {
        this.importErrors.push({
          category: 'Product Loss',
          location: this.getCellLocation(3, 10),
          message: this.jsonErrorMessages['productLoss_missing'],
          sheetName: 'Project',
        });
      }
    }
  }

  async validateParsedScenarioMaterialData(
    parsedData: ComponentDetails,
    baseRowIndex: number,
    projectConfigCountryNames: any,
    scenarioSheetName: any
  ) {
    const isNew = parsedData.templateVersion === 'new';

    // Column offsets (1-based for getCellLocation) that differ between OLD
    // and NEW templates.
    // Cols A–G (1..7) are the same in both formats.
    // OLD: H=ConversionCountry I=ConversionProcess J=Reuse K=NoOfTrips
    //      L=TransportMode N=Origin P=Destination
    // NEW: starts with CONVERSION_BLOCK_COUNT 4-col conversion blocks at H,
    //      then Reuse, NoOfTrips, TransportMode (leg1), [empty], Origin,
    //      [empty], Destination. Indices below are derived from
    //      CONVERSION_BLOCK_COUNT so adding more processes only requires
    //      bumping the constant in parseComponentRowNew.
    const N = UploadImportComponent.CONVERSION_BLOCK_COUNT;
    const B = UploadImportComponent.CONVERSION_BLOCK_SIZE;
    const C = UploadImportComponent.CONVERSION_START_COL; // 0-based start (H = 7)
    // 1-based column of first conversion block's Country cell (H + 1 = 9 = "I")
    const conversionCountryCol = isNew ? C + 2 : 8;
    // First column AFTER the conversion blocks, 1-based.
    const newReuseCol = C + N * B + 1; // P when N=2 → 16
    const noOfTripsCol = isNew ? newReuseCol + 1 : 11;
    const transportLeg1ModeCol = isNew ? newReuseCol + 2 : 12;
    const transportLeg1OriginCol = isNew ? newReuseCol + 4 : 14;
    const transportLeg1DestCol = isNew ? newReuseCol + 6 : 16;
    const transportLeg2StartCol = isNew ? newReuseCol + 8 : 18;
    const transportLeg2Stride = 4;
    const areaCm2ColPrimary = isNew ? C + 4 : 11; // K when N>=1 → 11

    const fieldsToValidate = [
      {
        key: 'componentName',
        category: 'Component Name',
        errorKey: 'componentName_missing',
        column: 2,
      },
      { key: 'type', category: 'Packaging Level', errorKey: 'packagingLevel_missing', column: 1 },
      { key: 'material', category: 'Material Name', errorKey: 'materialName_missing', column: 3 },
      {
        key: 'materialOrigin',
        category: 'Material Origin',
        errorKey: 'materialOrigin_missing',
        column: 7,
      },
      {
        key: 'weightVirgin',
        category: 'Component Weight',
        errorKey: 'componentWeight_missing',
        column: 5,
      },
      {
        key: 'weightOfPCR',
        category: 'Recycled Content',
        errorKey: 'recycledContent_missing',
        column: 6,
      },
      {
        key: 'weightOfPCR',
        category: 'Recycled Content',
        errorKey: 'recycledContent_tooHigh',
        column: 6,
      },
    ];

    fieldsToValidate.forEach(field => {
      const value = parsedData[field.key];
      const componentLabel = `${field.category}`;
      if (
        componentLabel === 'Recycled Content' &&
        parsedData.contentRecycled?.trim().toLowerCase() === 'yes' &&
        (value == null || (typeof value === 'string' && value.trim().length === 0))
      ) {
        this.importErrors.push({
          category: componentLabel,
          location: this.getCellLocation(baseRowIndex, field.column),
          message: this.jsonErrorMessages[field.errorKey],
          sheetName: scenarioSheetName,
        });
      }
      if (
        componentLabel != 'Recycled Content' &&
        (value == null || (typeof value === 'string' && value.trim().length === 0))
      ) {
        this.importErrors.push({
          category: componentLabel,
          location: this.getCellLocation(baseRowIndex, field.column),
          message: this.jsonErrorMessages[field.errorKey],
          sheetName: scenarioSheetName,
        });
      }
    });
    const maxRecycledContent = 100;
    if (
      parsedData.contentRecycled?.trim().toLowerCase() != 'None' &&
      parsedData.weightOfPCR != null &&
      Number(parsedData.weightOfPCR) > maxRecycledContent
    ) {
      this.importErrors.push({
        category: `Recycled Content`,
        location: this.getCellLocation(baseRowIndex, 6),
        message: this.jsonErrorMessages['recycledContent_tooHigh'],
        sheetName: scenarioSheetName,
      });
    }

    const projectCountries = projectConfigCountryNames
      .split(';')
      .map((country: string) => country.trim())
      .filter((country: string) => country !== '');

    if (parsedData.materialOrigin && !projectCountries.includes(parsedData.materialOrigin.trim())) {
      this.importErrors.push({
        category: 'Material Origin Country',
        location: this.getCellLocation(baseRowIndex, 7),
        message: this.jsonErrorMessages['country_missing'],
        sheetName: scenarioSheetName,
      });
    }

    // Validate every non-empty conversion leg's country (NEW template can
    // hold multiple conversions; OLD template falls back to the single
    // conversionPlace field).
    if (
      isNew &&
      Array.isArray((parsedData as any).conversionLegs) &&
      (parsedData as any).conversionLegs.length > 0
    ) {
      (parsedData as any).conversionLegs.forEach((leg: any, legIndex: number) => {
        if (
          leg?.conversionPlace &&
          !projectCountries.includes(String(leg.conversionPlace).trim())
        ) {
          const countryCol = (leg.column?.country ?? C + 1 + legIndex * B) + 1; // 0→1-based
          this.importErrors.push({
            category:
              legIndex === 0
                ? 'Conversion Country'
                : `Conversion Country (Process ${legIndex + 1})`,
            location: this.getCellLocation(baseRowIndex, countryCol),
            message: this.jsonErrorMessages['country_missing'],
            sheetName: scenarioSheetName,
          });
        }
      });
    } else if (
      parsedData.conversionPlace &&
      !projectCountries.includes(parsedData.conversionPlace.trim())
    ) {
      this.importErrors.push({
        category: 'Conversion Country',
        location: this.getCellLocation(baseRowIndex, conversionCountryCol),
        message: this.jsonErrorMessages['country_missing'],
        sheetName: scenarioSheetName,
      });
    }

    // Recycling pathway validation (column D, index 4) — same in both templates
    if (parsedData.recyclingPathway && String(parsedData.recyclingPathway).trim()) {
      const validPathways = ['yes', 'no', 'none', 'material default', 'open loop only'];
      if (!validPathways.includes(String(parsedData.recyclingPathway).trim().toLowerCase())) {
        this.importErrors.push({
          category: 'Recycling Pathway',
          location: this.getCellLocation(baseRowIndex, 4),
          message: this.jsonErrorMessages['recyclingPathway_invalid'],
          sheetName: scenarioSheetName,
        });
      }
    }

    // Area cm2 validation — only applies to NEW template. Each conversion
    // block has its own AreaBased toggle and its own AreaCm2 cell, so each
    // is validated independently.
    if (
      isNew &&
      Array.isArray((parsedData as any).conversionLegs) &&
      (parsedData as any).conversionLegs.length > 0
    ) {
      (parsedData as any).conversionLegs.forEach((leg: any, legIndex: number) => {
        if (leg?.isAreaBasedConversion && (!leg.areaCm2 || Number(leg.areaCm2) <= 0)) {
          const areaCol = (leg.column?.areaCm2 ?? C + 3 + legIndex * B) + 1; // 0→1-based
          this.importErrors.push({
            category: legIndex === 0 ? 'Area cm2' : `Area cm2 (Process ${legIndex + 1})`,
            location: this.getCellLocation(baseRowIndex, areaCol),
            message:
              'Area cm2 is required when area based conversion is enabled. Please enter a value greater than 0.',
            sheetName: scenarioSheetName,
          });
        }
      });
    } else if (isNew && parsedData.isAreaBasedConversion) {
      if (!parsedData.areaCm2 || Number(parsedData.areaCm2) <= 0) {
        this.importErrors.push({
          category: 'Area cm2',
          location: this.getCellLocation(baseRowIndex, areaCm2ColPrimary),
          message:
            'Area cm2 is required when area based conversion is enabled. Please enter a value greater than 0.',
          sheetName: scenarioSheetName,
        });
      }
    }

    if (parsedData.reuse?.trim().toLowerCase() === 'yes') {
      if (!parsedData.noOfTrips || Number(parsedData.noOfTrips) <= 0) {
        this.importErrors.push({
          category: `Reuse Trips`,
          location: this.getCellLocation(baseRowIndex, noOfTripsCol),
          message: this.jsonErrorMessages['reuseTrips_missing'],
          sheetName: scenarioSheetName,
        });
      }
    }

    // Material name validation — only if master data is loaded
    if (parsedData.material && String(parsedData.material).trim()) {
      const materialName = String(parsedData.material).trim();
      const materialItems = this.masterData?.componentMasterData?.materialItems;

      if (materialItems && materialItems.length > 0) {
        const allMaterials = materialItems.flatMap((item: any) => item.childItems || []);
        const materialFound = allMaterials.some(
          (child: any) =>
            child.description?.toLowerCase().trim() === materialName.toLowerCase() ||
            child.name?.toLowerCase().trim() === materialName.toLowerCase()
        );

        if (!materialFound) {
          this.importErrors.push({
            category: 'Material Name',
            location: this.getCellLocation(baseRowIndex, 3),
            message:
              coalesceNullish(
                this.jsonErrorMessages['materialName_notFound'],
                "The material name you entered doesn't match any available material. Please select a material from the dropdown list in the template."
              ) + ` ("${materialName}")`,
            sheetName: scenarioSheetName,
          });
        }
      }
    }

    // Note: Conversion process validation is handled during material mapping
    // (getConversionProcessByDetails) when master data is fully loaded.

    // Weight must be a positive number
    if (parsedData.weightVirgin != null && String(parsedData.weightVirgin).trim() !== '') {
      const weight = Number(parsedData.weightVirgin);
      if (isNaN(weight) || weight <= 0) {
        this.importErrors.push({
          category: 'Component Weight',
          location: this.getCellLocation(baseRowIndex, 5),
          message: coalesceNullish(
            this.jsonErrorMessages['componentWeight_invalid'],
            'Please enter a valid weight. The weight must be a number greater than zero (in grams).'
          ),
          sheetName: scenarioSheetName,
        });
      }
    }

    // Transport Legs validation — column offsets depend on the template version.
    if (parsedData.transportLegs && parsedData.transportLegs.length > 0) {
      parsedData.transportLegs.forEach((leg: any, legIndex: number) => {
        const legLabel = legIndex === 0 ? '' : ` (Leg ${legIndex + 1})`;
        const legColOffset =
          legIndex === 0
            ? transportLeg1ModeCol
            : transportLeg2StartCol + (legIndex - 1) * transportLeg2Stride;

        // Validate Transport Mode
        if (leg.transportMode && leg.transportMode.trim()) {
          const transportModeId = this.getTransportModeItemsId(leg.transportMode.trim());
          if (!transportModeId) {
            this.importErrors.push({
              category: `Transport Mode${legLabel}`,
              location: this.getCellLocation(baseRowIndex, legColOffset),
              message: coalesceNullish(
                this.jsonErrorMessages['transportMode_invalid'],
                'Invalid transport mode.'
              ),
              sheetName: scenarioSheetName,
            });
          }
        }

        // Validate Origin Location (Leg 1 only).
        // Port locations are allowed to be city-only (e.g. "Durres" — common
        // in the OLD template), so the "City, State, Country" requirement
        // only applies to non-port locations.
        if (legIndex === 0 && leg.originLocation && leg.originLocation.trim()) {
          const parsed = this.parseLocationString(leg.originLocation);
          const cityOnlyPortOk = parsed.isPort && !!parsed.city;
          if (!parsed.country && !cityOnlyPortOk) {
            this.importErrors.push({
              category: 'Origin Location',
              location: this.getCellLocation(baseRowIndex, transportLeg1OriginCol),
              message: 'Origin location must be in "City, State, Country" format.',
              sheetName: scenarioSheetName,
            });
          }
        }

        // Validate Destination Location
        if (leg.destinationLocation && leg.destinationLocation.trim()) {
          const parsed = this.parseLocationString(leg.destinationLocation);
          const cityOnlyPortOk = parsed.isPort && !!parsed.city;
          if (!parsed.country && !cityOnlyPortOk) {
            const destCol = legIndex === 0 ? transportLeg1DestCol : legColOffset + 2;
            this.importErrors.push({
              category: `Destination Location${legLabel}`,
              location: this.getCellLocation(baseRowIndex, destCol),
              message: 'Destination location must be in "City, State, Country" format.',
              sheetName: scenarioSheetName,
            });
          }
        }
      });
    }
  }

  validateParsedScenarioData(
    parsedData: ScenarioSpecificData,
    baseRowIndex: number,
    scenarioCount: any,
    scenarioSheetName: any,
    processLocation: any
  ) {
    const fieldsToValidate = [
      {
        key: 'scenarioName',
        category: 'Scenario Name',
        errorKey: 'scenarioName_missing',
        column: 2,
        rowOffset: 0,
      },
      // {
      //   key: 'supplierName',
      //   category: 'Supplier Name',
      //   errorKey: 'supplierName_missing',
      //   column: 7,
      //   rowOffset: 0,
      // },
      {
        key: 'nominalCapacity',
        category: 'Nominal Capacity',
        errorKey: 'nominalCapacity_missing',
        column: 10,
        rowOffset: 0,
      },
      // {
      //   key: 'description',
      //   category: 'Description',
      //   errorKey: 'description_missing',
      //   column: 2,
      //   rowOffset: 1,
      // },
      // {
      //   key: 'uuid',
      //   category: 'UUID',
      //   errorKey: 'uuid_missing',
      //   column: 2,
      //   rowOffset: 2,
      // },
      // {
      //   key: 'category',
      //   category: 'Category',
      //   errorKey: 'category_missing',
      //   column: 10,
      //   rowOffset: 2,
      // },
    ];

    if (!parsedData.endOfLifeCountry || parsedData.endOfLifeCountry.trim() === '') {
      this.importErrors.push({
        category: 'End Of Life Region',
        location: this.getCellLocation(6, 2),
        message: this.jsonErrorMessages['endOfLifeCountry_required'],
        sheetName: scenarioSheetName,
      });
    } else {
      if (!processLocation.includes(parsedData.endOfLifeCountry)) {
        this.importErrors.push({
          category: 'End Of Life Region',
          location: this.getCellLocation(6, 2),
          message: this.jsonErrorMessages['endOfLifeCountry_missing'],
          sheetName: scenarioSheetName,
        });
      }
    }

    fieldsToValidate.forEach(field => {
      const value = parsedData[field.key];
      const actualRow = baseRowIndex + field.rowOffset;

      if (value == null || (typeof value === 'string' && value.trim().length === 0)) {
        this.importErrors.push({
          category: field.category,
          location: this.getCellLocation(actualRow, field.column),
          message: this.jsonErrorMessages[field.errorKey],
          sheetName: scenarioSheetName,
        });
      } else if (value.length > 100) {
        this.importErrors.push({
          category: field.category,
          location: this.getCellLocation(actualRow, field.column),
          message: coalesceNullish(
            this.jsonErrorMessages[`${field.key}_tooLong`],
            `${field.category} is too long`
          ),
          sheetName: scenarioSheetName,
        });
      }
    });
  }

  getCellLocation(row: number, col: number): string {
    const columnLetter = String.fromCharCode(64 + col);
    return `${columnLetter} ${row}`;
  }

  removeImage(hiddenfileinput: any) {
    hiddenfileinput.value = null;
    this.isShowChange = false;
    this.selectedFiles = null;
  }

  onClose() {
    this.modal.close();
  }

  onSave() {
    if (this.templateType == ImportTypeEnum.CustomTemplate) {
      let hasError = false;

      if (!this.selectedTemplate) {
        this.templateTouched = true;
        hasError = true;
      }
      if (!this.startRow || this.startRow < 1) {
        this.startRowTouched = true;
        hasError = true;
      }
      if (!this.endRow || this.endRow < 1) {
        this.endRowTouched = true;
        hasError = true;
      }

      if (!this.selectedFiles) {
        this.extFile = true;
        hasError = true;
        return;
      }
      if (hasError) {
        return;
      }
    }
    if (!this.selectedFiles) {
      this.extFile = true;
      return;
    }

    if (this.importErrors.length >= 1) {
      return;
    }
    this.submitted = true;
    if (this.templateType == ImportTypeEnum.PiqetTemplate) {
      this.uploadImportProjectEvent.emit(this.importProject);
    } else if (this.templateType == ImportTypeEnum.MappingTemplate) {
      this.uploadImportProjectEvent.emit(this.jsonData);
    } else if (this.templateType == ImportTypeEnum.CustomTemplate) {
      if (this.excelData.length === 0) {
        this.importErrors.push({
          category: 'File Processing',
          message:
            'No project data found in the imported file. Please check the file and try again.',
        });
        return;
      }
      var data = {
        list: this.excelData,
        templateId: this.selectedTemplate?.id,
      };
      this.importCustomExcelEvent.emit(data);
    } else {
      this.uploadImportEvent.emit(this.formDataPayload);
    }
  }

  async getMasterDataForImportProject() {
    // Load main master data
    await new Promise<void>(resolve => {
      this.importProjectDataService.getMasterDataForImportProject().subscribe((res: any) => {
        if (res.messageType == 0) {
          this.masterData = res.data;
        }
        resolve();
      });
    });

    // If processItems is empty, load from Component API
    if (!this.masterData?.componentMasterData?.processItems?.length) {
      try {
        const componentData = await this.projectService.getAddEditPackMaterialModalAsync(
          0,
          0,
          true
        );
        if (componentData?.data) {
          if (!this.masterData.componentMasterData) {
            this.masterData.componentMasterData = {};
          }
          this.masterData.componentMasterData.processItems = coalesceNullish(
            componentData.data.processItems,
            []
          );
          this.masterData.componentMasterData.conversionProcessItems = coalesceNullish(
            componentData.data.conversionProcessItems,
            []
          );
          if (!this.masterData.componentMasterData.suppliersItems?.length) {
            this.masterData.componentMasterData.suppliersItems = coalesceNullish(
              componentData.data.suppliersItems,
              []
            );
          }
        }
      } catch (err) {
        // silently ignore
      }
    }
  }

  //MasterData Map in central model

  async mapProjectData() {
    const excelData = this.importProject;
    const projectConfig = this.projectImportInfo.ProjectConfiguration;

    projectConfig.projectId = 0;
    const projectNameSafe = coalesceNullish(excelData.generalInformation?.projectName, '');
    projectConfig.projectName = projectNameSafe.substring(0, 100);
    projectConfig.shortProjectName = projectNameSafe.substring(0, 100);
    projectConfig.description = coalesceNullish(excelData.generalInformation?.goalAndScope, '');
    projectConfig.systemBoundaryJustification = '';
    projectConfig.ImpectCategoryJustification = '';
    projectConfig.otherAssumptionsOrLimitation = '';
    projectConfig.sectorsIdentifierId = await this.getProductSectorItemsId(
      excelData.generalInformation.productLoss
    );
    projectConfig.isTurnOffCost =
      coalesceNullish(excelData.generalInformation?.turnOnCosts, '').toLowerCase() === 'yes'
        ? false
        : true;
    projectConfig.shareId = await this.getShareIdByName(excelData.generalInformation?.share);
    projectConfig.statusId = await this.getStatusIdByName(excelData.generalInformation?.status);
    projectConfig.countryIds = await this.getCountryIds(
      coalesceNullish(excelData.generalInformation?.processLocations?.split(';'), []).map(
        (name: string) => name.trim()
      )
    );
    projectConfig.countryId = await this.getCountryId(excelData.generalInformation?.defaultCountry);
    projectConfig.notModal = true;
    projectConfig.packageAccountedProductId = await this.getProjectScopeId(
      excelData.generalInformation?.projectScope
    );
    this.scopePresetId = projectConfig.packageAccountedProductId;

    await this.changeProductLossScope(projectConfig.sectorsIdentifierId);
    await this.importProjectDataService.updateSwitchOnCost(projectConfig.isTurnOffCost);
    await this.getPackagingData();
    projectConfig.packagingLCStages = this.payloadPackagingLCStages;
    projectConfig.productLCStages = this.payloadProductLCStages;
    projectConfig.packagingLevels = this.payloadPackagingLevels;

    if (excelData.generalInformation?.share === ShareOption.OneOfMyTeam) {
      projectConfig.teamId = await this.getTeamIdByName(excelData.generalInformation?.teamName);
    }

    if (!projectConfig.isTurnOffCost) {
      projectConfig.currencyId = await this.getCurrencyIdByName(
        excelData.generalInformation?.currency
      );
    }

    const scenarios: Scenario[] = [];
    let scenarioIdCounter = 1;

    for (const scenarioData of coalesceNullish(excelData.scenarioSpecificData, [])) {
      try {
        const scenarioNameSafe = coalesceNullish(scenarioData.scenarioName, '');
        const unit = this.masterData?.productPackagingContentConfiguration?.unit?.find(
          (u: any) =>
            u.description?.trim().toLowerCase() ===
            coalesceNullish(scenarioData.nominalCapacity, '').toString().trim().toLowerCase()
        );

        const scenario: Scenario = {
          name: scenarioNameSafe,
          owner: '',
          packageScenarioId: 0,
          projectId: projectConfig.projectId,
          scenarioCode: '',
          scenarioId: scenarioIdCounter,
          scenarioImage: '',
          scenarioPictureFileName: '',
          scenarioStatusId: 1, // Set default Draft status
          scenarioTypeId: 0,
          status: this.masterData?.scenarioMasterData?.scenarioDetail?.statusItems.find(
            (item: any) => item.id == 1
          ),
          description: coalesceNullish(scenarioData.description, ''),
          scenarioNamePackaging: coalesceNullish(scenarioData.scenarioNamePackaging, ''),
          shortScenarioName: scenarioNameSafe.substring(0, 15),
          category: coalesceNullish(scenarioData.category, ''),
          department: coalesceNullish(scenarioData.department, ''),
          year: coalesceNullish(scenarioData.year, ''),
          subCategory: coalesceNullish(scenarioData.subCategory, ''),
          uuid: coalesceNullish(scenarioData.uuid, ''),
          productClassId: 0,
          productFamilyId: 0,
          productDepartmentId: 0,
          isScenarioSpecificCategorization: false,
          orderId: scenarioIdCounter++,
          packagingLevelInfoList: await this.updatePackagingLevelIds(
            scenarioData.packagingLevelDetail.filter((item: any) => item.isActive)
          ),
          materialList: [],
          scenarioProductDetail: {
            scenarioId: 0,
            packageScenarioId: 0,
            functionalUnitsPerProduct: 1,
            massVolume: coalesceBlank(scenarioData.massVolumeRetailPack, 1),
            unitId: unit?.id,
            bulkDensity: 1,
            annualRetailVolume: coalesceBlank(scenarioData.annualRetailVolumeUnits, 0),
            sectorIdentifierId: coalesceNullish(projectConfig.sectorsIdentifierId, 0),
            isSecIdentifierReq: false,
          },
          productDetailList: [],
          palletConfiguration: {
            id: 0,
            palletReusable: true,
            palletReuse: '50+ reuses',
            palletTypeId: 15,
            scenarioId: 0,
            isModified: false,
          },
          endOfLifeCountryId: await this.getCountryId(scenarioData.endOfLifeCountry),
          extraDetails: {
            id: 0,
            scenarioId: 0,
            unitsPerBox: coalesceNullish(scenarioData.unitsPerBox, ''),
            boxesPerLayer: coalesceNullish(scenarioData.boxesPerLayer, ''),
            layersXPallet: coalesceNullish(scenarioData.layersPerPallet, ''),
            totalWeightPerBox: coalesceNullish(scenarioData.totalWeightPerBox, ''),
            totalHeight: coalesceNullish(scenarioData.totalHeight, ''),
            totalWeight: coalesceNullish(scenarioData.totalWeight, ''),
            supplierComments: coalesceNullish(scenarioData.supplierComments, ''),
            productType: coalesceNullish(scenarioData.productType, ''),
            packDescription: coalesceNullish(scenarioData.packDescription, ''),
            material: coalesceNullish(scenarioData.material, ''),
            renewableEnergy: coalesceNullish(scenarioData.renewableEnergy, ''),
            manufacturedEMEA: coalesceNullish(scenarioData.manufacturedEMEA, ''),
            supplierItemCode: coalesceNullish(scenarioData.supplierItemCode, ''),
            packCode: coalesceNullish(scenarioData.packCode, ''),
          },
        };
        scenarios.push(scenario);
      } catch (scenarioErr) {
        // Skip failed scenario
      }
    }

    // Material Mapping — with error protection per component
    const materialList: MaterialDetails[] = [];
    let componentIdCounter = 1;

    // Pre-cache country IDs for all used countries (faster than sequential)
    const allCountryNames = new Set<string>();
    for (const scenarioData of coalesceNullish(excelData.scenarioSpecificData, [])) {
      for (const comp of coalesceNullish(scenarioData.componentDetails, [])) {
        if (comp.materialOrigin) allCountryNames.add(String(comp.materialOrigin).trim());
        if (comp.conversionPlace) allCountryNames.add(String(comp.conversionPlace).trim());
        for (const leg of coalesceNullish((comp as any).conversionLegs, [])) {
          if (leg?.conversionPlace) allCountryNames.add(String(leg.conversionPlace).trim());
        }
      }
    }
    // Pre-fetch all country IDs in parallel
    const countryIdCache: Record<string, number> = {};
    await Promise.all(
      [...allCountryNames].map(async name => {
        countryIdCache[name] = await this.getCountryId(name);
      })
    );

    // Pre-resolve every transport leg location in a single round-trip. Without
    // this, each leg triggers its own /City/GetAllCityForCustomOriginAutoComplete
    // call inside resolveLocation, which dominates import time for large files.
    await this.prefetchTransportLegLocations(excelData);

    for (const scenario of scenarios) {
      const scenarioData = excelData.scenarioSpecificData.find(
        (data: any) => data.scenarioName === scenario.name
      );

      if (scenarioData?.componentDetails) {
        for (const component of scenarioData.componentDetails) {
          try {
            const packageLevelId = await this.findPackagingLevelId(
              this.masterData?.scenarioMasterData?.scenarioDetail?.packagingLevelInfoList,
              component.type
            );

            let reuseType = 'Retail reuse';
            const lowerCaseType = coalesceNullish(component.type?.trim().toLowerCase(), '');
            if (['sub-retail', 'retail'].includes(lowerCaseType)) {
              reuseType = 'Consumer reuse';
            }

            const materialOrigin = String(coalesceNullish(component.materialOrigin, '')).trim();
            const conversionPlace = String(coalesceNullish(component.conversionPlace, '')).trim();
            const supplierNameSafe = coalesceNullish(scenarioData.supplierName, '');
            const isAreaBasedConv = coalesceNullish(component.isAreaBasedConversion, false);
            const areaCm2Num = Number(coalesceNullish(component.areaCm2, 0));

            // Build transport items safely
            let transportItems: any[] = [];
            try {
              transportItems = await this.buildTransportFromLegs(
                coalesceNullish(component.transportLegs, [])
              );
            } catch (transportErr) {
              // Skip failed transport
            }

            // Get conversion process(es) safely. The new template can carry up
            // to N conversion processes per component (see CONVERSION_BLOCK_COUNT
            // in parseComponentRowNew). Each leg becomes one ConversionProcessItem
            // and they're merged into a single conversionItems array — that array
            // is what flows through to the project as multiple conversion steps.
            let conversionItems: any[] = [];
            try {
              const legs =
                Array.isArray((component as any).conversionLegs) &&
                (component as any).conversionLegs.length > 0
                  ? (component as any).conversionLegs
                  : [
                      {
                        conversionPlace,
                        conversionProcess: String(coalesceNullish(component.conversionProcess, '')),
                        isAreaBasedConversion: isAreaBasedConv,
                        areaCm2: areaCm2Num,
                      },
                    ];
              for (const leg of legs) {
                const legProcess = String(coalesceNullish(leg.conversionProcess, '')).trim();
                if (!legProcess) continue;
                const legCountry = String(coalesceNullish(leg.conversionPlace, '')).trim();
                const legIsAreaBased = coalesceNullish(leg.isAreaBasedConversion, false);
                const legArea = Number(coalesceNullish(leg.areaCm2, 0));
                const items = await this.getConversionProcessByDetails(
                  componentIdCounter,
                  legCountry,
                  legProcess,
                  supplierNameSafe,
                  legIsAreaBased,
                  legArea
                );
                if (Array.isArray(items) && items.length) {
                  conversionItems.push(...items);
                }
              }
            } catch (convErr) {
              // Skip failed conversion
            }

            const material: MaterialDetails = {
              name: String(coalesceNullish(component.componentName, '')).substring(0, 50),
              scenarioId: scenario.scenarioId,
              componentId: componentIdCounter++,
              packageLevelId,
              materialSupplierId: 0,
              materialSupplierName: supplierNameSafe,
              isCustomMaterial: false,
              transportaionId: 2,
              materialCountryId: coalesceNullish(countryIdCache[materialOrigin], 0),
              countryName: materialOrigin,
              materialWeight: Number(coalesceNullish(component.weightVirgin, 0)),
              recycledContent: Number(coalesceNullish(component.weightOfPCR, 0)),
              materialCost: 0,
              conversionCountryId: coalesceNullish(countryIdCache[conversionPlace], 0),
              conversionPlaceName: conversionPlace,
              customtransportScenarioId: null,
              customTransoportScenarioDetailViewModels: [],
              conversionSupplierId: 0,
              conversionProcessItems: conversionItems,
              distanceTransportationItems: [],
              locationTransportationItems: transportItems,
              isRecommendedConversionProcess: !isAreaBasedConv,
              isAreaBaseConversionProcess: isAreaBasedConv,
              isAreaConversionProcess: isAreaBasedConv,
              areaBaseConversionProcess: isAreaBasedConv ? areaCm2Num : 0,
              areaConversionId: coalesceNullish(conversionItems?.[0]?.areaConversionId, null),
              isCustomConversion: false,
              carbonNeutral: false,
              fscCertification: false,
              otherCertification: false,
              conversionProcessName: String(
                coalesceNullish(component.conversionProcess, '')
              ) as any,
              materialId: await this.getMaterialId(component.material),
              packageLevelInfoName: coalesceNullish(component.type, ''),
              materialName: String(coalesceNullish(component.material, '')),
              recylcableComponent: await this.getRecyclableComponentId(
                String(coalesceNullish(component.recyclingPathway, 'Material default'))
              ),
              conversionSupplierName: supplierNameSafe,
              isReuse: String(coalesceNullish(component.reuse, '')).toLowerCase() === 'yes',
              noOfTrips: coalesceNullish(component.noOfTrips, 0),
              reuseType,
              orderNo: componentIdCounter,
            };
            materialList.push(material);
            scenario.materialList.push(material);
          } catch (compError) {
            // Skip failed component
            // Continue with next component — don't break the entire import
          }
        }
      }
    }

    const updatedProjectInfo = this.importProjectDataService.projectInfo;
    updatedProjectInfo.ProjectConfiguration = projectConfig;
    updatedProjectInfo.ScenarioList = scenarios;
    await this.updatePackagingLevel();
    await this.importProjectDataService.updateProjectImportInfo(updatedProjectInfo);
  }

  updatePackagingLevel() {
    const updatedProjectInfo = this.importProjectDataService.projectInfo;
    const updatedList: PackagingLevelDetailScenario[] = updatedProjectInfo.ScenarioList.map(
      (existingScenario: any) => {
        const filteredScenario: PackagingLevelDetailScenario = {
          scenarioId: existingScenario.scenarioId ?? 0,
          packagingLevelDetail:
            existingScenario.packagingLevelInfoList?.filter(
              (item: any) => item.isActive === true
            ) ?? [],
        };
        return filteredScenario;
      }
    );
    this.importProjectDataService.updatePackagingLevelDetailInfo(updatedList);
  }

  updatePackagingLevelIds(data: any) {
    data.forEach((item: any, index: number, array: any[]) => {
      const matchingDetail =
        this.masterData.scenarioMasterData.scenarioDetail.packagingLevelInfoList.find(
          (detail: any) => detail.name === item.name
        );
      if (matchingDetail) {
        item.packagingLevelId = matchingDetail.packagingLevelId;
        item.imageName = matchingDetail.imageName;
      }

      item.isLastChild = index === array.length - 1;
    });

    return data;
  }

  // Material
  getConversionProcessByDetails(
    id: any,
    countryName: string,
    processName: string,
    supplierName: string,
    isAreaBased: boolean = false,
    areaCm2: number = 0
  ): ConversionProcessItem[] {
    const conversionProcessItems: ConversionProcessItem[] = [];
    if (!processName) {
      return conversionProcessItems;
    }

    // Try to find process ID from master data
    let conversionProcessId = this.getProcessIdByChildName(processName);
    let areaConversionId: number | null = null;

    // If not found, try case-insensitive search with description field
    if (!conversionProcessId && this.masterData?.componentMasterData?.processItems) {
      const normalizedName = processName.toLowerCase().trim();
      for (const processItem of this.masterData.componentMasterData.processItems) {
        const child = processItem.childItems?.find(
          (c: any) =>
            c.name?.toLowerCase().trim() === normalizedName ||
            c.description?.toLowerCase().trim() === normalizedName
        );
        if (child) {
          conversionProcessId = child.id;
          break;
        }
      }
    }

    // If still not found, try from Identifiers sheet conversion data
    if (!conversionProcessId && this.masterData?.componentMasterData?.conversionProcessItems) {
      const normalizedName = processName.toLowerCase().trim();
      const match = this.masterData.componentMasterData.conversionProcessItems?.find(
        (item: any) =>
          item.name?.toLowerCase().trim() === normalizedName ||
          item.description?.toLowerCase().trim() === normalizedName
      );
      if (match) {
        conversionProcessId = match.id;
      }
    }

    // For area-based conversion, resolve from areaConversionItems (nested: categories → childItems)
    if (isAreaBased && this.masterData?.componentMasterData?.areaConversionItems) {
      const normalizedName = processName.toLowerCase().trim();
      // Search through category childItems for the matching identifier
      for (const category of this.masterData.componentMasterData.areaConversionItems) {
        if (category.childItems && category.childItems.length > 0) {
          const childMatch = category.childItems.find(
            (child: any) =>
              child.name?.toLowerCase().trim() === normalizedName ||
              child.description?.toLowerCase().trim() === normalizedName
          );
          if (childMatch) {
            areaConversionId = childMatch.id;
            break;
          }
        }
        // Also check parent category level
        if (
          category.name?.toLowerCase().trim() === normalizedName ||
          category.description?.toLowerCase().trim() === normalizedName
        ) {
          areaConversionId = category.id;
        }
      }
    }

    const conversionProcessItem = new ConversionProcessItem();
    conversionProcessItem.componentId = id - 1;
    conversionProcessItem.conversionCountryId = this.getCountryId(countryName);
    conversionProcessItem.conversionCountryName = countryName;
    conversionProcessItem.conversionProcessId = isAreaBased ? 0 : conversionProcessId;
    conversionProcessItem.conversionProcessItems = [];
    conversionProcessItem.conversionProcessName = processName;
    conversionProcessItem.conversionSupplierId = null;
    conversionProcessItem.conversionSupplierName = supplierName;
    conversionProcessItem.id = 0;
    conversionProcessItem.isAreaBaseConversionProcess = isAreaBased;
    conversionProcessItem.areaBaseConversionProcess = isAreaBased ? areaCm2 : 0;
    conversionProcessItem.areaConversionId = areaConversionId;
    conversionProcessItem.isCostTrunOff = false;
    conversionProcessItem.isCustomConversion = false;
    conversionProcessItem.isRecommendedConversionProcess = !isAreaBased;
    conversionProcessItems.push(conversionProcessItem);
    return conversionProcessItems;
  }

  /**
   * Parse "City, State, Country" or "City, State, Country (Port)" location string.
   * Returns { city, state, country, isPort } — `isPort` is true when the
   * original string ended with "(Port)" (case-insensitive, optional whitespace).
   */
  parseLocationString(location: string): {
    city: string;
    state: string;
    country: string;
    isPort: boolean;
  } {
    if (!location || !location.trim()) {
      return { city: '', state: '', country: '', isPort: false };
    }
    const trimmed = location.trim();
    const isPort = /\(Port\)\s*$/i.test(trimmed);
    const cleaned = trimmed.replace(/\s*\(Port\)\s*$/i, '').trim();
    const parts = cleaned.split(',').map(p => p.trim());
    if (parts.length >= 3) {
      return { city: parts[0], state: parts[1], country: parts[parts.length - 1], isPort };
    } else if (parts.length === 2) {
      return { city: parts[0], state: '', country: parts[1], isPort };
    }
    return { city: coalesceNullish(parts[0], ''), state: '', country: '', isPort };
  }

  /**
   * Collect every transport-leg location across the parsed Excel and resolve
   * them all server-side in a single POST. The matched cities are written into
   * `countryCache` under the same `resolved_<loc>_port_<isPort>` keys that
   * `resolveLocation` (in buildTransportFromLegs) reads — so per-leg lookups
   * become cache hits and the slow per-call autocomplete round-trips disappear.
   */
  async prefetchTransportLegLocations(excelData: any): Promise<void> {
    const locationSet = new Set<string>();
    for (const scenarioData of coalesceNullish(excelData?.scenarioSpecificData, [])) {
      for (const component of coalesceNullish(scenarioData?.componentDetails, [])) {
        for (const leg of coalesceNullish(component?.transportLegs, [])) {
          const o = leg?.originLocation ? String(leg.originLocation).trim() : '';
          const d = leg?.destinationLocation ? String(leg.destinationLocation).trim() : '';
          if (o) locationSet.add(o);
          if (d) locationSet.add(d);
        }
      }
    }
    if (locationSet.size === 0) return;

    const locations = [...locationSet];
    let resolved: any[] = [];
    try {
      const res = await this.sharedService.resolveLocationsBulk(locations);
      resolved = coalesceNullish(res?.data, []) ?? [];
    } catch {
      return;
    }

    const normalize = (s: any): string =>
      String(coalesceNullish(s, '')).replace(/\s+/g, ' ').trim().toLowerCase();

    for (const item of resolved) {
      if (!item?.location || !item?.found || !item?.idStr) continue;
      const parsed = this.parseLocationString(String(item.location));
      const cacheKey = `resolved_${normalize(item.location)}_port_${parsed.isPort}`;
      this.countryCache[cacheKey] = {
        idStr: String(item.idStr),
        name: String(coalesceNullish(item.name, item.location)),
      };
    }
  }

  /**
   * Build transport items from transport legs
   * Supports multiple legs with "City, State, Country" format locations
   */
  async buildTransportFromLegs(transportLegs: any[]): Promise<TransportDetails[]> {
    if (!transportLegs || transportLegs.length === 0) return [];

    // Search the autocomplete endpoint, which returns Name already formatted as
    // "City, State, Country" or "City, State, Country (Port)" — the same format
    // produced in the dropdown and in the Excel cell. Matching against this
    // formatted name lets us disambiguate cities sharing a raw name (e.g.
    // German Hamburg vs. PNG "Hamburg (Pg) Harbor") without a separate
    // country lookup.
    const searchAutoComplete = async (term: string, isPort: boolean) => {
      if (!term || !term.trim()) return null;
      const cacheKey = `autocomplete_${term.trim().toLowerCase()}_port_${isPort}`;
      if (!this.countryCache[cacheKey]) {
        const data = await this.sharedService.getAllCityForCustomOriginAutoComplete(
          term.trim(),
          isPort
        );
        this.countryCache[cacheKey] = data;
      }
      return this.countryCache[cacheKey];
    };

    const normalize = (s: any): string =>
      String(coalesceNullish(s, '')).replace(/\s+/g, ' ').trim().toLowerCase();

    // Strip a trailing "(Port)" suffix and normalize for comparison.
    const stripPort = (s: any): string =>
      normalize(String(coalesceNullish(s, '')).replace(/\s*\(Port\)\s*$/i, ''));

    const pickBestMatch = (
      cityList: any[],
      locationStr: string,
      parsed: { city: string; state: string; country: string; isPort: boolean }
    ): any => {
      if (!cityList?.length) return null;

      const targetFull = normalize(locationStr);
      const targetNoPort = stripPort(locationStr);
      const targetCity = normalize(parsed.city);
      const targetState = normalize(parsed.state);
      const targetCountry = normalize(parsed.country);

      // 1. Exact full-string match including the "(Port)" suffix.
      const fullMatch = cityList.find((c: any) => normalize(c.name) === targetFull);
      if (fullMatch) return fullMatch;

      // 2. Exact match ignoring the "(Port)" suffix on either side.
      const noPortMatch = cityList.find((c: any) => stripPort(c.name) === targetNoPort);
      if (noPortMatch) return noPortMatch;

      // 3. Match by city + state + country tokens parsed from the formatted Name.
      if (targetCity && targetCountry) {
        const tokenMatch = cityList.find((c: any) => {
          const tokens = stripPort(c.name)
            .split(',')
            .map((t: string) => t.trim());
          if (tokens.length < 2) return false;
          const cityTok = tokens[0];
          const countryTok = tokens[tokens.length - 1];
          const stateTok = tokens.length >= 3 ? tokens[1] : '';
          if (cityTok !== targetCity || countryTok !== targetCountry) return false;
          return !targetState || !stateTok || stateTok === targetState;
        });
        if (tokenMatch) return tokenMatch;
      }

      // 4. Match by city + country only (state may be missing in either side).
      if (targetCity && targetCountry) {
        const cityCountryMatch = cityList.find((c: any) => {
          const tokens = stripPort(c.name)
            .split(',')
            .map((t: string) => t.trim());
          if (tokens.length < 2) return false;
          return tokens[0] === targetCity && tokens[tokens.length - 1] === targetCountry;
        });
        if (cityCountryMatch) return cityCountryMatch;
      }

      // 5. Last resort: first row whose city token matches.
      if (targetCity) {
        const cityOnly = cityList.find((c: any) => {
          const tokens = stripPort(c.name)
            .split(',')
            .map((t: string) => t.trim());
          return tokens[0] === targetCity;
        });
        if (cityOnly) return cityOnly;
      }

      return null;
    };

    // Resolve a "City, State, Country" or "City, State, Country (Port)" location
    // string to a city object. The port flag comes from the location string
    // itself (the "(Port)" suffix), not from the transport mode — so a single
    // leg can have a non-port origin and a port destination.
    const resolveLocation = async (locationStr: string): Promise<any> => {
      const parsed = this.parseLocationString(locationStr);
      if (!parsed.city && !parsed.country) return null;

      const isPort = parsed.isPort;

      // Cache key includes the port flag so a non-port leg doesn't reuse a
      // cached port resolution for the same location string (and vice versa).
      const resolvedCacheKey = `resolved_${normalize(locationStr)}_port_${isPort}`;
      if (this.countryCache[resolvedCacheKey]) {
        return this.countryCache[resolvedCacheKey];
      }

      let result: any = null;

      // Primary lookup: search the autocomplete endpoint by city name. Its
      // response carries the fully formatted "City, State, Country (Port)"
      // name, which we match against the Excel cell text.
      if (parsed.city) {
        const cityData = await searchAutoComplete(parsed.city, isPort);
        if (cityData?.data?.length) {
          result = pickBestMatch(cityData.data, locationStr, parsed);
        }
      }

      // Fallback: search by country term when city-search returned nothing
      // (e.g. the cell only had the country, or the city is misspelt).
      if (!result && parsed.country) {
        const countryData = await searchAutoComplete(parsed.country, isPort);
        if (countryData?.data?.length) {
          result = pickBestMatch(countryData.data, locationStr, parsed);
          if (!result) {
            result = countryData.data[0];
          }
        }
      }

      if (result) {
        this.countryCache[resolvedCacheKey] = result;
      }

      return result;
    };

    const getDistance = async (
      origin: string,
      destination: string,
      originId: string,
      destinationId: string,
      transportModeId: number
    ): Promise<string> => {
      const key = `${origin.toLowerCase()}-${destination.toLowerCase()}-${transportModeId}`;
      if (!this.distanceCache[key]) {
        this.distanceCache[key] = await this.sharedService.getDistanceByTransportMode(
          origin,
          destination,
          originId,
          destinationId,
          transportModeId
        );
      }
      return this.distanceCache[key];
    };

    const transportItems: TransportDetails[] = [];
    let indexCounter = 1;

    for (let legIdx = 0; legIdx < transportLegs.length; legIdx++) {
      try {
        const leg = transportLegs[legIdx];
        if (!leg) continue;

        const transportModeId = leg.transportMode
          ? coalesceNullish(this.getTransportModeItemsId(String(leg.transportMode).trim()), 19)
          : 19;
        const transportModeName = leg.transportMode
          ? String(leg.transportMode).trim()
          : 'Ship, International';

        // Parse origin location (Leg 1 only has origin) — port flag is now
        // derived from the "(Port)" suffix in the location string itself.
        if (legIdx === 0 && leg.originLocation && String(leg.originLocation).trim()) {
          try {
            const originCity = await resolveLocation(String(leg.originLocation));
            if (originCity) {
              transportItems.push({
                customSiteTransportId: 0,
                id: 0,
                alwaysOrigin: true,
                originId: originCity.idStr,
                originName: originCity.name,
                transportModeName: '',
                distance: 0,
                isOrigin: true,
                isCustomTruck: false,
                isPalletLoad: false,
                productId: 0,
                locationDistance: 0,
                ingredientTransportId: 0,
                unit: 'km',
                transportModeId: 0,
                persanteageMassLoad: 0,
                index: indexCounter++,
                idStr: originCity.idStr,
              });
            }
          } catch (originErr) {
            // Skip failed origin
          }
        }

        // Parse destination location (all legs have destination)
        if (leg.destinationLocation && String(leg.destinationLocation).trim()) {
          try {
            const destCity = await resolveLocation(String(leg.destinationLocation));
            if (destCity) {
              // Calculate distance safely
              let distance: any = 0;
              try {
                if (transportItems.length > 0) {
                  const prevItem = transportItems[transportItems.length - 1];
                  distance = await getDistance(
                    prevItem.originName,
                    destCity.name,
                    String(coalesceNullish(prevItem.originId, 0)),
                    String(coalesceNullish(destCity.idStr, 0)),
                    transportModeId
                  );
                }
              } catch (distErr) {
                // Skip failed distance
              }

              transportItems.push({
                customSiteTransportId: 0,
                id: 0,
                alwaysOrigin: false,
                originId: destCity.idStr,
                originName: destCity.name,
                transportModeName: transportModeName,
                transportModeId: transportModeId,
                distance: coalesceBlank(distance, 0),
                isOrigin: false,
                isCustomTruck: false,
                isPalletLoad: false,
                productId: 0,
                locationDistance: 0,
                ingredientTransportId: 0,
                unit: 'km',
                persanteageMassLoad: 0,
                index: indexCounter++,
                idStr: destCity.idStr,
              });
            }
          } catch (destErr) {
            // Skip failed destination
          }
        }
      } catch (legErr) {
        // Skip failed leg
        // Continue with next leg
      }
    }

    return transportItems;
  }

  getProcessIdByChildName(childName: string): number {
    if (!childName || !this.masterData?.componentMasterData?.processItems) return 0;
    const normalizedChildName = childName.toLowerCase().trim();
    for (const processItem of coalesceNullish(
      this.masterData.componentMasterData.processItems,
      []
    )) {
      const child = processItem.childItems?.find(
        (childItems: any) => childItems.name?.toLowerCase().trim() === normalizedChildName
      );

      if (child) {
        return child.id;
      }
    }
    return 0;
  }

  // Enhanced conversion process lookup — searches name + description across all sources
  findConversionProcessId(processName: string): number {
    if (!processName || !processName.trim()) return 0;

    // Try standard lookup first
    let id = this.getProcessIdByChildName(processName);
    if (id) return id;

    const normalizedName = processName.toLowerCase().trim();

    // Try description field
    if (this.masterData?.componentMasterData?.processItems) {
      for (const processItem of coalesceNullish(
        this.masterData.componentMasterData.processItems,
        []
      )) {
        const child = processItem.childItems?.find(
          (c: any) => c.description?.toLowerCase().trim() === normalizedName
        );
        if (child) return child.id;
      }
    }

    // Try conversionProcessItems if available
    if (this.masterData?.componentMasterData?.conversionProcessItems) {
      const match = this.masterData.componentMasterData.conversionProcessItems.find(
        (item: any) =>
          item.name?.toLowerCase().trim() === normalizedName ||
          item.description?.toLowerCase().trim() === normalizedName
      );
      if (match) return match.id;
    }

    return 0;
  }

  findPackagingLevelId(data: any[], inputString: string): number {
    const searchString = inputString.trim().toLowerCase();
    for (const packaging of data) {
      if (
        packaging.name?.toLowerCase() === searchString ||
        packaging.packagingLevelName?.toLowerCase() === searchString
      ) {
        return coalesceNullish(packaging.packagingLevelId, packaging.id);
      }
    }
    return 0;
  }
  getMaterialId(name: any) {
    const matchedChildItems = this.masterData.componentMasterData.materialItems
      .flatMap((item: any) => coalesceNullish(item.childItems, []))
      .filter((child: any) => child.description == name);
    return matchedChildItems[0]?.id;
  }
  getTransportModeItemsId(name: any) {
    const matchedItem = this.masterData.componentMasterData.transportModeItems.find(
      (item: any) => item.name === name
    );
    return matchedItem?.id;
  }
  getProductSectorItemsId(name: any) {
    const matchedItem = this.masterData.projectMasterData.productSectorItems.find(
      (item: any) => item.description === name
    );
    return matchedItem?.id;
  }

  getRecyclableComponentId(status: string): number {
    const recyclableItems = this.masterData.componentMasterData.recylcableComponentItems;

    const item = recyclableItems.find(
      (item: any) => item.name.toLowerCase() === status.toLowerCase()
    );

    return coalesceNullish(item?.id, 3); // "Material default"
  }

  // Project configuration
  getShareIdByName(name: string): number {
    const match = this.masterData.projectMasterData.shareItems.find(
      (item: { id: number; name: string }) => item.name === name
    );
    return match ? match.id : 0;
  }

  getStatusIdByName(name: string): number {
    const match = this.masterData.projectMasterData.shareItems.find(
      (item: { id: number; name: string }) => item.name === name
    );

    return match ? match.id : coalesceNullish(this.masterData.projectMasterData?.statusId, 0);
  }

  getCountryIds(countryNames: string[]): number[] {
    return countryNames.map(name => this.getCountryId(name)).filter(id => id !== 0);
  }

  getCountryId(countryName: string): number {
    const country =
      countryName?.trim().toLowerCase() === 'eu'
        ? CountryWithRegion.Europe.trim().toLowerCase()
        : countryName != null
        ? countryName
        : '';

    const match = this.masterData.projectMasterData.regionItems.find(
      (item: { id: number; name: string }) =>
        item?.name
          ?.replace(CountryWithRegion.GlobalByProxy, '')
          .replace(CountryWithRegion.EuropeByProxy, '')
          ?.trim()
          .toLowerCase() === country.trim().toLowerCase()
    );

    return match ? match.id : 0;
  }

  getProjectScopeId(scopeDescription: ProjectScopeType): number {
    const scopeMapping = {
      'Simple retail package ignoring product': 3,
      'Retail, traded and pallet package supply chain logistics': 1,
      'Full package and product LCA': 2,
    };

    return coalesceNullish(scopeMapping[scopeDescription], 0);
  }

  async getPackagingData() {
    const res = await this.projectService
      .getProjectScopeByIdPresets(this.scopePresetId, 0)
      .toPromise();
    this.payloadPackagingLCStages = res.data.packagingLCStages;
    this.payloadProductLCStages = res.data.productLCStages;
    this.payloadPackagingLevels = res.data.packagingLevels;
    this.payloadProductWestModeling = res.data.productWestModeling;
  }

  getTeamIdByName(teamName: string): number {
    const team = this.masterData.projectMasterData.projectTeamList.find(
      (item: { name: string }) => item.name === teamName
    );
    return team ? team.id : 0;
  }

  getCurrencyIdByName(currencyName: string): number {
    const currency = this.masterData.projectMasterData.currencyItems.find(
      (item: { name: string }) => item.name === currencyName
    );
    return currency ? currency.id : 1;
  }
  // project configuration end

  getGroupedErrors(errors: any[]): ErrorGroup[] {
    const groupedErrors = errors.reduce((acc, error) => {
      const sheetName = error.sheetName;
      if (!acc[sheetName]) {
        acc[sheetName] = {
          sheetName,
          errors: [],
        };
      }
      acc[sheetName].errors.push(error);
      return acc;
    }, {});

    return Object.values(groupedErrors);
  }

  validateField() {
    if (this.startRow && this.startRow >= 1) {
      this.startRowTouched = false;
    }

    if (this.endRow && this.endRow >= 1) {
      this.endRowTouched = false;
    }
  }

  getImportCustomTemplates() {
    this.importProjectDataService.getTemplatesByOrganization().subscribe((res: any) => {
      if (res.data) {
        this.importLogListData = res.data.map((item: any) => {
          return Object.keys(item).reduce((acc: any, key: string) => {
            acc[key] =
              item[key] !== null && item[key] !== undefined && item[key].toString().trim() !== ''
                ? item[key]
                : 'N/A';
            return acc;
          }, {});
        });
      }
    });
  }

  selectTemplate(data: any) {
    this.selectedTemplate = data;
  }

  onTemplateChange() {
    if (this.selectedTemplate) {
      this.templateTouched = false;
    } else {
      this.templateTouched = true;
    }
  }

  onWeightChange() {
    if (this.selectedWeight) {
      this.weightTouched = false;
    } else {
      this.weightTouched = true;
    }
  }

  onWeightBlur() {
    this.weightTouched = true;
    this.cdRef.detectChanges();
  }

  onTemplateBlur() {
    this.templateTouched = true;
    this.cdRef.detectChanges();
  }

  async onTemplateTypeChange(type: any) {
    this.templateType = type;

    if (type === ImportTypeEnum.CustomTemplate) {
      this.selectedTemplate = null;
      this.selectedWeight = null;
      this.startRow = null;
      this.endRow = null;
    }

    this.templateTouched = false;
    this.weightTouched = false;
    this.startRowTouched = false;
    this.endRowTouched = false;
    if (this.templateType == ImportTypeEnum.PiqetTemplate) {
      await this.loadComingSoonText();
      await this.getMasterDataForImportProject();
    }
    if (this.templateType == ImportTypeEnum.CustomTemplate) {
      await this.getImportCustomTemplates();
    }
  }
}
